import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IVideoClipResource, ResourceType, ResourceStatus, IVideoClip } from '@packages/global';
import { episodeApi } from '../../services/user/episode.api';
import { videoClipResourceApi } from '../../services/user/videoClipResource.api';

type ImageResourceType = 'all' | 'start' | 'end' | 'storyboard';

interface EpisodeImageSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string, resource: IVideoClipResource) => void;
  episodeId: string;
}

interface ImageResourceWithClip extends IVideoClipResource {
  clipSequence?: number;
  clipTitle?: string;
}

export default function EpisodeImageSelector({ isOpen, onClose, onSelect, episodeId }: EpisodeImageSelectorProps) {
  const { t } = useTranslation(['common_ui', 'common']);
  const [activeTab, setActiveTab] = useState<ImageResourceType>('all');
  const [imageResources, setImageResources] = useState<ImageResourceWithClip[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedResource, setSelectedResource] = useState<ImageResourceWithClip | null>(null);

  const loadResources = async () => {
    if (!episodeId) return;

    setLoading(true);
    try {
      // 1. 获取集的所有视频片段
      const videoClips = await episodeApi.getEpisodeVideoClips(episodeId);
      if (videoClips.length === 0) {
        setImageResources([]);
        return;
      }

      // 2. 批量获取所有片段的资源
      const clipIds = videoClips.map(clip => clip.id);
      const resourcesMap = await videoClipResourceApi.getBatchResourcesStatus(clipIds);

      // 3. 创建 clipId -> clip 的映射
      const clipMap = new Map<string, IVideoClip>();
      videoClips.forEach(clip => clipMap.set(clip.id, clip));

      // 4. 筛选图片类型且已完成的资源
      const imageTypes = [ResourceType.START_IMAGE, ResourceType.END_IMAGE, ResourceType.STORYBOARD_IMAGE];
      const allImageResources: ImageResourceWithClip[] = [];

      Object.entries(resourcesMap).forEach(([clipId, resources]) => {
        const clip = clipMap.get(clipId);
        resources.forEach(resource => {
          if (
            imageTypes.includes(resource.resourceType) &&
            resource.status === ResourceStatus.COMPLETED &&
            resource.resultUrl
          ) {
            allImageResources.push({
              ...resource,
              clipSequence: clip?.sequence,
              clipTitle: clip?.title
            });
          }
        });
      });

      // 5. 按片段序号排序
      allImageResources.sort((a, b) => (a.clipSequence || 0) - (b.clipSequence || 0));

      setImageResources(allImageResources);
    } catch (error) {
      console.error('加载图片资源失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && episodeId) {
      setSelectedResource(null);
      loadResources();
    }
  }, [isOpen, episodeId]);

  // 根据 tab 筛选资源
  const filteredResources = useMemo(() => {
    if (activeTab === 'all') return imageResources;

    const typeMap: Record<ImageResourceType, ResourceType | null> = {
      'all': null,
      'start': ResourceType.START_IMAGE,
      'end': ResourceType.END_IMAGE,
      'storyboard': ResourceType.STORYBOARD_IMAGE
    };

    const targetType = typeMap[activeTab];
    return targetType ? imageResources.filter(r => r.resourceType === targetType) : imageResources;
  }, [imageResources, activeTab]);

  const handleSelect = () => {
    if (selectedResource && selectedResource.resultUrl) {
      onSelect(selectedResource.resultUrl, selectedResource);
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[90]">
      <div className="bg-bg-surface rounded-lg w-[900px] h-[680px] flex flex-col border border-border-subtle shadow-2xl">
        {/* Header */}
        <div className="flex items-center p-4 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-gray-50">{t('common_ui:episodeImageSelector.title')}</h2>
          <div className="flex bg-bg-elevated rounded-lg p-1 ml-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'all'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:episodeImageSelector.tabAll')}
            </button>
            <button
              onClick={() => setActiveTab('start')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'start'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:episodeImageSelector.tabStart')}
            </button>
            <button
              onClick={() => setActiveTab('end')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'end'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:episodeImageSelector.tabEnd')}
            </button>
            <button
              onClick={() => setActiveTab('storyboard')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'storyboard'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:episodeImageSelector.tabStoryboard')}
            </button>
          </div>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-200 cursor-pointer">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              <p>{t('common_ui:episodeImageSelector.empty')}</p>
              <p className="text-sm mt-2">{t('common_ui:episodeImageSelector.emptyHint')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  onClick={() => setSelectedResource(resource)}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${selectedResource?.id === resource.id
                    ? 'border-blue-500 ring-2 ring-blue-400'
                    : 'border-border-subtle hover:border-border-default'
                    }`}
                >
                  <div className="aspect-video">
                    <img
                      src={resource.resultUrl}
                      alt={t('common_ui:episodeImageSelector.clipLabel', { seq: resource.clipSequence, title: t(`common_ui:episodeImageSelector.resourceType.${resource.resourceType}`) })}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* 选中标记 */}
                  {selectedResource?.id === resource.id && (
                    <div className="absolute top-2 left-2">
                      <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                        ✓
                      </div>
                    </div>
                  )}

                  {/* 资源类型标签 */}
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${resource.resourceType === ResourceType.START_IMAGE
                      ? 'bg-green-600 text-white'
                      : resource.resourceType === ResourceType.END_IMAGE
                        ? 'bg-orange-600 text-white'
                        : 'bg-purple-600 text-white'
                      }`}>
                      {t(`common_ui:episodeImageSelector.resourceType.${resource.resourceType}`)}
                    </span>
                  </div>

                  {/* 底部信息 */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <div className="text-white text-xs">
                      <div className="truncate">
                        {t('common_ui:episodeImageSelector.clipLabel', { seq: resource.clipSequence, title: resource.clipTitle || t('common_ui:episodeImageSelector.unnamed') })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border-subtle bg-bg-elevated">
          <div className="text-sm text-gray-400">
            {t('common_ui:episodeImageSelector.totalImages', { count: filteredResources.length })}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 bg-bg-surface border border-border-subtle rounded-lg hover:bg-bg-elevated cursor-pointer transition-colors"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedResource}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {t('common_ui:episodeImageSelector.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
